import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ClipboardCheck } from 'lucide-react';
import { Card } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { usePendingActions } from './usePendingActions';
import PendingActionsPanel from './PendingActionsPanel';

const DASHBOARD_TOP_N = 5;

const RowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3 px-4 py-3">
    <Skeleton circle className="h-9 w-9 flex-none" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-48 max-w-full" />
      <Skeleton className="h-3 w-32 max-w-full" />
    </div>
    <Skeleton className="h-7 w-24 flex-none" />
  </div>
);

/**
 * Dashboard-embedded Pending Actions inbox. Replaces the passive approvals
 * pill strip and the standalone onboarding widget: one place, real actions.
 * Renders nothing when the user arrives with nothing pending — the dashboard
 * should not carry an empty box. Once items HAVE been shown, clearing the
 * last one keeps the card mounted as a "caught up" confirmation so layout
 * and keyboard focus don't vanish mid-interaction.
 */
const PendingActionsCard: React.FC = () => {
  const { t } = useTranslation('requests');
  const query = useMemo(() => ({ pageSize: 25 }), []);
  const { data, loading, error, revalidate } = usePendingActions(query);

  // Whether this mount has ever shown pending items (distinguishes
  // "arrived with zero" from "just cleared the last one").
  const [hadItems, setHadItems] = useState(false);
  const totalCount = data?.totalCount ?? 0;
  useEffect(() => {
    if (totalCount > 0) setHadItems(true);
  }, [totalCount]);

  // Nothing was ever pending (or the inbox failed) → yield the space. The
  // TopBar badge and the full page remain the durable entry points.
  if (!loading && !hadItems && (error || !data || totalCount === 0)) return null;

  const allDone = hadItems && !loading && !!data && totalCount === 0;

  return (
    <Card variant="elevated" className="mb-6" data-testid="pa-dashboard-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={16} className="text-muted-foreground" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-foreground">{t('Pending actions')}</h2>
          {data && totalCount > 0 && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-primary">
              {totalCount}
            </span>
          )}
        </div>
      </div>
      <div className="py-3">
        {loading && !data ? (
          <div aria-hidden="true">
            <RowSkeleton />
            <RowSkeleton />
            <RowSkeleton />
          </div>
        ) : allDone ? (
          <div className="flex items-center gap-2.5 px-4 py-2" role="status" data-testid="pa-dashboard-done">
            <CheckCircle2 size={18} className="flex-none text-success" aria-hidden="true" />
            <p className="text-sm text-foreground">{t("You're all caught up")}</p>
          </div>
        ) : (
          data && (
            <PendingActionsPanel
              variant="dashboard"
              items={data.items}
              countsByGroup={data.countsByGroup}
              totalCount={data.totalCount}
              onChanged={revalidate}
              topN={DASHBOARD_TOP_N}
            />
          )
        )}
      </div>
    </Card>
  );
};

export default PendingActionsCard;
